import { ClampLines } from "./styles/ClampLines/ClampLines.mjs";
import { DotSeparator, dotText } from "./styles/DotSeparator/DotSeparator.mjs";
import { jsx } from "react/jsx-runtime";
import { useApolloClient } from "@apollo/client";
import { Autosuggest, Box } from "braid-design-system";
import React, { useState, useEffect } from "react";
import gql from "graphql-tag";
import { RelatedSearch, TitledContent } from "./styles/RelatedSearch/RelatedSearch.mjs";
const GET_KEYWORD_SUGGESTIONS = gql`
  query getKeywordSuggestions(
    $keyword: String!
    $country: CountryCodeIso2!
    $visitorId: UUID
  ) {
    searchKeywordsSuggest(
      query: $keyword
      count: 10
      countryCode2: $country
      visitorId: $visitorId
    ) {
      metadata {
        requestToken
        flightId
      }
      suggestions {
        ... on KeywordSuggestion {
          text
          highlights {
            start
            end
          }
        }
      }
    }
  }
`;
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
};
const mapTextToValue = (arr) => (arr || []).map((el) => ({ ...el, value: el.text }));
const mapSuggestionsResponse = (data) => {
  const { searchKeywordsSuggest } = data || {
    searchKeywordsSuggest: { suggestions: [] }
  };
  return mapTextToValue(searchKeywordsSuggest.suggestions);
};
const noop = () => {
};
const KeywordAutoSuggest = React.forwardRef(
  ({
    initialSuggestions = [],
    keyword = { text: "" },
    onChange,
    onClear = noop,
    onBlur = noop,
    country,
    delay = 200,
    visitorId,
    keywordAutosuggestV2,
    label,
    ariaLabelledby,
    ...rest
  }, ref) => {
    const client = useApolloClient();
    const debouncedKeywordText = useDebounce(keyword.text, delay);
    const debouncedKeywordLength = Object.keys(debouncedKeywordText).length;
    const [storedSuggestions, setStoredSuggestions] = useState([]);
    const [metadata, setMetadata] = useState({});
    const showSuggestions = !keywordAutosuggestV2 && debouncedKeywordLength > 1 || keywordAutosuggestV2 && debouncedKeywordLength > 0;
    useEffect(() => {
      if (showSuggestions) {
        client.query({
          query: GET_KEYWORD_SUGGESTIONS,
          variables: { country, keyword: debouncedKeywordText, visitorId },
          fetchPolicy: "network-only"
        }).then(({ data }) => {
          var _a, _b;
          const suggestions = mapSuggestionsResponse(data);
          setStoredSuggestions(suggestions);
          setMetadata({
            requestToken: (_a = data.searchKeywordsSuggest.metadata) == null ? void 0 : _a.requestToken,
            flightId: (_b = data.searchKeywordsSuggest.metadata) == null ? void 0 : _b.flightId
          });
        }).catch((error) => {
          if (error) {
            console.error("KeywordSuggestion", error);
          }
        });
      }
    }, [showSuggestions, client, country, debouncedKeywordText, visitorId]);
    const onKeywordChange = ({
      text,
      value
    }) => {
      const isSuggestion = value !== void 0;
      const keywordRank = isSuggestion ? storedSuggestions.findIndex((suggestion) => suggestion.text === text) : void 0;
      const keywordListCount = isSuggestion ? storedSuggestions.length : void 0;
      const newKeyword = {
        text,
        value,
        keywordRank,
        keywordListCount,
        previousText: debouncedKeywordText,
        isSuggestion,
        suggestionMetadata: isSuggestion ? metadata : void 0
      };
      onChange(newKeyword);
    };
    const onClearLocal = () => {
      setStoredSuggestions([]);
      onClear();
    };
    return /* @__PURE__ */ jsx(
      Autosuggest,
      {
        ref,
        suggestions: showSuggestions ? storedSuggestions : initialSuggestions || [],
        data: { "hj-allow": true },
        value: keyword,
        onChange: onKeywordChange,
        onClear: onClearLocal,
        onBlur,
        label,
        "aria-labelledby": ariaLabelledby,
        ...rest
      }
    );
  }
);
const paddingX = { mobile: "small", desktop: "xlarge" };
const PageInset = ({ children }) => /* @__PURE__ */ jsx(Box, { paddingX, children });
PageInset.paddingX = paddingX;
const Space = ({ size }) => /* @__PURE__ */ jsx(Box, { paddingTop: size, paddingLeft: size });
export {
  ClampLines,
  DotSeparator,
  KeywordAutoSuggest,
  PageInset,
  RelatedSearch,
  Space,
  TitledContent,
  dotText
};
